import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';


// Projected lateness for a day's jobs: { [quoteId]: { late_minutes, projected_start, ... } }
export const useLateness = (authFetch, date, sig) => {
  const [jobs, setJobs] = useState({});
  useEffect(() => {
    if (!date) return undefined;
    let live = true;
    authFetch('get', `/lateness?date=${date}`).then(({ data }) => { if (live) setJobs(data.jobs || {}); }).catch(() => {});
    const t = setInterval(() => authFetch('get', `/lateness?date=${date}`).then(({ data }) => { if (live) setJobs(data.jobs || {}); }).catch(() => {}), 60000);
    return () => { live = false; clearInterval(t); };
  }, [authFetch, date, sig]);
  return jobs;
};

export const fmtLate = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ''}`.trim() : `${m}m`);

export const LateBadge = ({ p, ref_ }) => (p ? (
  <span className="inline-flex items-center gap-0.5 whitespace-nowrap shrink-0 bg-red-600 text-white text-[10px] font-black px-1 py-0.5 rounded" title={`Booked ${p.booked_start} · now about ${p.projected_start}–${p.projected_end}`} data-testid={`late-badge-${ref_}`}>
    <Clock className="w-3 h-3 shrink-0" />+{fmtLate(p.late_minutes)}<span className="hidden xl:inline"> · ~{p.projected_start}</span>
  </span>
) : null);

const QUICK = [15, 30, 45, 60, 90];

// Set how late a job will start; later jobs for that driver are recalculated from it.
export const RunningLate = ({ job, authFetch, onSaved, projection }) => {
  const [mins, setMins] = useState(job.running_late || '');
  const [busy, setBusy] = useState(false);
  const ref = job.ref || (job.id || '').slice(0, 6).toUpperCase();
  const save = async (v) => {
    setBusy(true);
    try {
      await authFetch('post', `/jobs/${job.id}/running-late`, { minutes: Number(v) || 0 });
      toast.success(Number(v) ? `Marked ${fmtLate(Number(v))} late. Later jobs recalculated.` : 'Running late cleared.');
      setMins(Number(v) || '');
      onSaved?.();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : "Couldn't save."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-md border border-red-200 bg-red-50/40 p-2" data-testid={`running-late-${ref}`}>
      <div className="text-[10px] font-black uppercase tracking-wider text-red-800 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Running late on this job?</div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {QUICK.map(q => <button key={q} type="button" disabled={busy} onClick={() => save(q)} className={`h-8 px-2 rounded-md text-xs font-bold border ${Number(mins) === q ? 'bg-red-600 text-white border-red-600' : 'bg-white border-slate-200 text-[color:var(--mv-navy)]'}`} data-testid={`late-quick-${q}-${ref}`}>+{q}m</button>)}
        <input type="number" min="0" max="600" value={mins} onChange={(e) => setMins(e.target.value)} placeholder="mins" className="h-8 w-16 rounded-md border border-slate-200 px-1.5 text-xs" data-testid={`late-input-${ref}`} />
        <button type="button" disabled={busy} onClick={() => save(mins)} className="h-8 px-2 rounded-md bg-[color:var(--mv-navy)] text-white text-xs font-bold" data-testid={`late-save-${ref}`}>Save</button>
        {job.running_late ? <button type="button" disabled={busy} onClick={() => save(0)} className="h-8 px-2 rounded-md border border-slate-200 bg-white text-xs font-bold" data-testid={`late-clear-${ref}`}>On time again</button> : null}
      </div>
      {projection && <div className="text-[11px] text-red-800 mt-1" data-testid={`late-projection-${ref}`}>Now starting about <strong>{projection.projected_start}</strong> (booked {projection.booked_start}), finishing about {projection.projected_end}.</div>}
      <div className="text-[10px] text-slate-500 mt-0.5">Later jobs are worked out using each job's booked hours plus drive time between jobs.</div>
    </div>
  );
};

